import { isPast } from 'date-fns'

import { HTTPError } from '@mondough/error-handling'
import { getEnv } from '@mondough/next-utils'

import {
  CACHE_EXPIRATION_MS,
  cacheName,
  clearAuth,
  fetchClientTokenFromAPI,
  getAccessToken,
  logoutAndRedirectClientErrorCodes,
  refreshUserAccessToken,
  retryableClientErrorCodes,
  syncSession,
  timeStoreCacheName,
} from './'

const normaliseContentTypeHeader = (ct: string | null | undefined): string =>
  ct == null ? 'text/plain' : ct.trim().toLowerCase().replace(/;.+$/, '')

type Options = Partial<{
  method: string
  authorizationType: 'none' | 'user' | 'client'
  useCache: boolean
  body: BodyInit | null
  headers?: HeadersInit
  retryOnExpiredToken: boolean
}>

const apiFetch = async <ResponseData>(
  input: string,
  options: Options = {},
): Promise<ResponseData> => {
  const {
    authorizationType = 'user',
    useCache = false,
    retryOnExpiredToken = true,
    ...fetchOptions
  } = options
  let authType = authorizationType
  const prependedInput = `${getEnv('apiPath')}${input}`

  if (authType === 'user' && getAccessToken('user') == null) {
    authType = 'client' // If the user token is missing (logged out pages), use the client token
  }
  if (authType === 'client' && getAccessToken('client') == null) {
    await fetchClientTokenFromAPI() // If the client token is missing, fetch it
  }

  const authedOptions = {
    ...(authType === 'none'
      ? undefined
      : {
          headers: {
            authorization: `Bearer ${getAccessToken(authType)}`,
          },
        }),
    ...fetchOptions,
  }

  let response: Response | undefined

  if (useCache) {
    const cache = await caches.open(cacheName)
    const cachedTime = await getCachedTime(prependedInput, timeStoreCacheName)
    if (!isPast(cachedTime + CACHE_EXPIRATION_MS)) {
      response = await cache.match(prependedInput)
    } else {
      await cache.delete(prependedInput)
    }
  }

  if (!response) {
    response = await fetch(prependedInput, authedOptions)
    if (useCache) {
      const cache = await caches.open(cacheName)
      await cache.put(prependedInput, response.clone())
      await setCachedTime(prependedInput, timeStoreCacheName)
    }
  }

  const contentType = normaliseContentTypeHeader(
    response.headers.get('Content-Type'),
  )

  let json
  switch (contentType) {
    case 'application/json':
      json = await response.json()
      break
    case 'text/plain':
      json = { body: await response.text() }
      break
    case 'image/jpeg':
    case 'image/png':
    case 'image/svg+xml':
      json = await response.blob()
      break
    default:
      throw new HTTPError(response, `Unexpected content type: ${contentType}`)
  }

  if (response.ok === true) return json

  const error = new HTTPError(response, json)
  //@ts-ignore - code is not always present in the response
  const code = error.body?.code

  // If the error is because of an expired access token, refresh the token and retry the request
  if (
    Object.values(retryableClientErrorCodes).includes(code) &&
    retryOnExpiredToken
  ) {
    try {
      if (authType === 'client') {
        await fetchClientTokenFromAPI()
      } else {
        await refreshUserAccessToken()
        await syncSession()
      }
    } catch (e) {
      //Swallow the error as this might be caused by concurrent requests trying to refresh the token
    }

    return apiFetch(input, options)
  }

  if (logoutAndRedirectClientErrorCodes.includes(code)) {
    await clearAuth(true)
  }

  throw error
}

export default apiFetch

// Function to set cache time
async function setCachedTime(url: string, cacheName: string) {
  const timeStore = await caches.open(cacheName)
  const timeResponse = new Response(Date.now().toString())
  await timeStore.put(url, timeResponse)
}

// Function to get cache time
async function getCachedTime(url: string, cacheName: string) {
  const timeStore = await caches.open(cacheName)
  const timeResponse = await timeStore.match(url)
  if (timeResponse) {
    const timeText = await timeResponse.text()
    return parseInt(timeText, 10)
  }
  return 0
}
